<template>
  <div v-if="def">
    <div class="jps-timeline-view" :style="totalHeight">
      <div class="jps-time-background"></div>
      <div class="jps-time-base" :class="{ 'jps-time-base-max-width' : isJps }">
        <span v-if="showStartEnd" class="jps-time-exp start-time">{{
          def.timeline.start
        }}</span>
        <span v-if="showStartEnd" class="jps-time-exp end-time">{{
          def.timeline.end
        }}</span>
        <span
          v-for="(time, index) in times"
          class="jps-time-entry"
          :class="timeClass(time)"
          :style="calcWidth(time)"
          :key="time.name.ja"
          @click="clickTime(index)"
        >
          <div
            class="pointing-line"
            v-if="showLegend"
            :style="calcLineHeight(time)"
          />
          <span
            class="legend"
            v-if="showLegend"
            :style="calcLegendHeight(time)"
            >{{ ls(time.name) }}</span
          >
        </span>
      </div>
    </div>
    <table class="timeline-table" v-if="showTable">
      <tbody>
        <tr
          v-for="(tp, index) in def.parts"
          @click="clickTime(index)"
          :class="{ selected: selected === index }"
        >
          <td>{{ ls(tp.time.name) }}</td>
          <td>{{ ls(tp.description) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" setup>
const { isJps } = useServiceType()
const labelHeight = 24
const labelBaseMargin = 16

const labelDiff = 4

const { ls } = useI18n()

interface Props {
  def: PartTimeline
  selected: number
}
const props = withDefaults(defineProps<Props>(), {})

const emits = defineEmits(['click-time'])

const showTable = computed(() => {
  return props.def.layout === 'table'
})

const showLegend = computed(() => {
  return props.def.layout !== 'table'
})

const showStartEnd = computed(() => {
  return props.def.layout === 'withyears'
})

const times = computed(() => {
  return props.def.parts.map(tp => tp.time)
})

const timeClass = (time: Time) => {
  return {
    'jps-single': time.start === time.end,
    'jps-selected':
      props.def.parts[props.selected] &&
      props.def.parts[props.selected].time === time,
  }
}

const calcLineHeight = (time: Time) => {
  return {
    height: `${
      time.labelHeight * labelHeight +
      (times.value[props.selected] === time ? -labelDiff - 11 : -5)
    }px`,
    top: `${times.value[props.selected] === time ? labelDiff + 30 : +20}px`,
  }
}

const calcLegendHeight = (time: Time) => {
  return {
    top: `${
      time.labelHeight * labelHeight +
      (times.value[props.selected] === time ? labelDiff : 0) +
      labelBaseMargin
    }px`,
  }
}

const totalHeight = computed(() => {
  return {
    height: `${
      times.value.map(t => t.labelHeight).reduce((p, c) => (p > c ? p : c), 0) *
        labelHeight +
      labelBaseMargin * 2
    }px`,
  }
})

const clickTime = (i: number) => {
  emits('click-time', i)
}

const calcWidth = (time: Time) => {
  let min = calcPos(
    time.start + (time.startFraction ? time.startFraction : 0),
  )
  let max = calcPos(time.end + (time.endFraction ? time.endFraction : 0))
  if (time.start !== time.end) return `left:${min}%;width:${max - min}%;`
  else return `left:${min}%;`
}

const calcPos = (n: number) => {
  return Math.min(
    Math.max(
      0,
      (100 * (n - props.def.timeline.start)) /
        (props.def.timeline.end - props.def.timeline.start),
    ),
    100,
  )
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

$size: 16px;
$sizeLarge: 36px;

.jps-timeline-view {
  position: relative;
  display: block;
  width: 100%;
  margin-top: 1.5rem;
  margin: 1.5rem auto 0 auto;
  width: calc(100% - ($size * 2));

  .jps-time-exp {
    font-size: 0.875rem;
    position: absolute;
    top: -1.5rem;
    &.start-time {
      left: 0;
      transform: translateX(-50%);
    }
    &.end-time {
      right: 0;
      transform: translateX(50%);
    }
  }

  .jps-names {
    margin-bottom: 3px;
    span:not(:first-child) {
      margin-left: 1rem;
    }
  }

  $height: 6px;

  .jps-time-background {
    height: $height;
    width: 100%;
    background: $jps-gray-300;
    position: absolute;
    border-radius: 0.1rem;
    top: 0;
    left: 0;
  }

  .jps-time-base {
    display: block;
    margin: 0 auto;
    position: relative;
    width: 100%;
    height: $height;
    border-radius: 0.1rem;
    background: $jps-gray-900;
    @include is-mobile() {
      max-width: 80vw;
    }
  }
  .jps-time-base-max-width {
    max-width: $curation-content-width;
  }
  .jps-time-entry {
    /* transition: $transition; */
    display: block;
    position: absolute;
    top: 0;
    height: $size;
    cursor: pointer;
    /* border-radius: 6px; */
    background: $jps-gray-900;
    transform: translate(0, -6px);
    &.jps-selected {
      border: 6px solid $jps-key-red;
      background: $white;
      height: $sizeLarge;
      transform: translate(0, -16px);
      .legend {
        /* font-weight: bold; */
      }
    }
    &.jps-single {
      top: 0;
      height: $size;
      width: $size;
      border-radius: 999px;
      transform: translate(-50%, -6px);
      &:hover {
        background: $jps-gray-900;
      }
      &.jps-selected,
      &.jps-selected:hover {
        top: 0;
        height: $sizeLarge;
        width: $sizeLarge;
        transform: translate(-50%, -16px);
        background: $white;
        border: 6px solid $jps-key-red;
      }
    }
    .pointing-line {
      width: 1px;
      background-color: $jps-gray-900;
      position: absolute;
      left: 50%;
    }
    .legend {
      display: block;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: max-content;
    }
  }
}
.timeline-table {
  border-spacing: 0 4px;
  border-collapse: separate;
  @include is-mobile() {
    min-width: 100%;
    padding: 0.5rem 0;
  }
  @include is-tablet() {
    min-width: 100%;
    padding: 0.5rem 0;
  }
  @include is-desktop() {
    min-width: lcontent-width;
    max-width: $wide-content-width;
    margin-left: auto;
    margin-right: auto;
  }
  tr {
    cursor: pointer;

    &:nth-child(even) {
      background-color: $jps-gray-50;
    }

    td {
      padding: 8px;
      &:first-child {
        border-top-left-radius: $global-radius-small;
        border-bottom-left-radius: $global-radius-small;
      }
      &:last-child {
        border-top-right-radius: $global-radius-small;
        border-bottom-right-radius: $global-radius-small;
      }
    }
    &.selected {
      background-color: #f6eeee;
      font-weight: bold;
      td {
        border: 2px solid $jps-key-red;
        border-style: solid solid solid none;
        &:first-child {
          border-style: solid none solid solid;
        }
        &:last-child {
          border-style: solid solid solid none;
        }
      }
    }
  }
}
</style>
